/**
 * ApiTokenRow — W2.4
 *
 * Eine Zeile in der API-Tokens-Liste.
 * Name, last_used_at (relative Zeit), created_at.
 * Rotieren-Button mit Confirm-Dialog.
 * Widerrufen-Button mit Confirm-Dialog.
 * Touch-Targets >= 44px.
 */
import { RefreshCw, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatLastUsed, formatDate } from './tokenUtils'
import { DemoGuardedButton } from '../../components/DemoGuardedButton'
import type { ApiToken } from './useApiTokens'

type Props = {
  token: ApiToken
  isRotating: boolean
  isRevoking: boolean
  onRotate: (id: number) => void
  onRevoke: (id: number) => void
}

function SpinnerIcon() {
  return (
    <span
      className="spinner"
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite',
      }}
    />
  )
}

export function ApiTokenRow({ token, isRotating, isRevoking, onRotate, onRevoke }: Props) {
  const { t } = useTranslation('developer')

  function handleRotate() {
    if (window.confirm(t('apiTokens.row.rotateConfirm'))) {
      onRotate(token.id)
    }
  }

  function handleRevoke() {
    if (window.confirm(t('apiTokens.row.revokeConfirm'))) {
      onRevoke(token.id)
    }
  }

  const isBusy = isRotating || isRevoking
  const lastUsed = formatLastUsed(token.last_used_at)

  return (
    <div
      className="list-row"
      role="listitem"
      data-testid={`token-row-${token.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      {/* Identity */}
      <div className="identity-meta" style={{ flex: 1, minWidth: 0 }}>
        <div className="identity-label" data-testid={`token-name-${token.id}`}>
          {token.name}
        </div>
        <div className="identity-sub" style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>
          <span data-testid={`token-last-used-${token.id}`}>
            {lastUsed ?? t('apiTokens.row.neverUsed')}
          </span>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>{t('apiTokens.row.createdAt', { date: formatDate(token.created_at) })}</span>
        </div>
      </div>

      {/* Rotieren-Button */}
      <DemoGuardedButton
        className="btn btn-icon btn-ghost"
        onClick={handleRotate}
        disabled={isBusy}
        aria-label={t('apiTokens.row.rotateBtnLabel', { name: token.name })}
        aria-busy={isRotating}
        data-testid={`btn-rotate-${token.id}`}
        style={{ minWidth: 44, minHeight: 44, flexShrink: 0 }}
        title={t('apiTokens.row.rotateBtnTitle')}
      >
        {isRotating ? <SpinnerIcon /> : <RefreshCw size={16} aria-hidden="true" />}
      </DemoGuardedButton>

      {/* Widerrufen-Button */}
      <DemoGuardedButton
        className="btn btn-icon btn-ghost"
        onClick={handleRevoke}
        disabled={isBusy}
        aria-label={t('apiTokens.row.revokeBtnLabel', { name: token.name })}
        aria-busy={isRevoking}
        data-testid={`btn-revoke-${token.id}`}
        style={{
          minWidth: 44,
          minHeight: 44,
          flexShrink: 0,
          color: 'var(--danger)',
        }}
        title={t('apiTokens.row.revokeBtnTitle')}
      >
        {isRevoking ? <SpinnerIcon /> : <Trash2 size={16} aria-hidden="true" />}
      </DemoGuardedButton>
    </div>
  )
}
